import React, { useState, useEffect } from 'react';
import {
  List, ListItem, ListItemText, Typography, Button, CircularProgress, Alert, Box, Paper, TextField, FormControl, InputLabel, Select, MenuItem, Container, Avatar, Grid, IconButton
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import LaunchIcon from '@mui/icons-material/Launch';
import { useAuth } from '../../context/AuthContext';
import {
  updateCharacter,
  getRaceOptions,
  getClassOptions,
  uploadCharacterImage
} from '../../services/characterService';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { v4 as uuidv4 } from 'uuid';

/**
 * Formats character class display string with levels
 */
const formatCharacterClasses = (classes, totalLevel) => {
  if (!classes || classes.length === 0) return `Level ${totalLevel || 'N/A'}`;
  const classString = classes.map(c => `${c.className} ${c.classLevel}`).join(' / ');
  return `${classString} (Total Level ${totalLevel})`;
};

/**
 * Character management page component
 * @param {Array} characters - Initial characters prop
 * @param {boolean} loadingCharacters - Initial loading state prop
 */
const MyCharactersPage = ({ characters: initialCharacters, loadingCharacters: initialLoading }) => {
  const { firestoreUser } = useAuth();
  const navigate = useNavigate();  const [characters, setCharacters] = useState(initialCharacters || []);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState('');

  const [editingCharacterId, setEditingCharacterId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [raceOptions, setRaceOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState('');

  const [editingImageForCharacterId, setEditingImageForCharacterId] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  /**
   * Updates local characters state when props change
   */
  useEffect(() => {
    setCharacters(initialCharacters || []);
  }, [initialCharacters]);

  /**
   * Manages loading state based on initial loading and options loading
   */
  useEffect(() => {
    if (initialLoading) {
        setLoading(true);
    } else if (!optionsLoading) {
        setLoading(false);
    }  }, [initialLoading, optionsLoading]);

  /**
   * Fetches race and class options on component mount
   */
  useEffect(() => {
    const fetchOptions = async () => {
      setOptionsLoading(true);
      try {
        const [racesData, classesData] = await Promise.all([
          getRaceOptions(),
          getClassOptions()
        ]);
        setRaceOptions(racesData);
        setClassOptions(classesData);
        setOptionsError('');
      } catch (err) {
        console.error("Failed to fetch race/class options:", err);
        setOptionsError('Failed to load character creation options. Some features may be unavailable.');
      } finally {
        setOptionsLoading(false);
        if (!initialLoading) {
            setLoading(false);
        }
      }
    };
    fetchOptions();
  }, [initialLoading]);

  const handleEditClick = (character) => {
    setEditingCharacterId(character.id);
    setEditFormData({
      name: character.name || '',
      race: character.race || '',
      dndBeyondLink: character.dndBeyondLink || '',
      classes: character.classes ? character.classes.map(cls => ({ ...cls, id: uuidv4() })) : [{ id: uuidv4(), className: '', classLevel: 1 }],
    });
  };

  const handleCancelEdit = () => {
    setEditingCharacterId(null);
    setEditFormData({});
  };
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditFormClassChange = (id, field, value) => {
    setEditFormData(prev => ({
      ...prev,
      classes: prev.classes.map(cls =>
        cls.id === id ? { ...cls, [field]: field === 'classLevel' ? Math.max(1, parseInt(value, 10) || 1) : value } : cls
      )
    }));
  };

  const handleEditFormAddClass = () => {
    setEditFormData(prev => {
      if (prev.classes.length < 5) {
        return {
          ...prev,
          classes: [...prev.classes, { id: uuidv4(), className: '', classLevel: 1 }]
        };
      }
      return prev; // Max 5 classes
    });
  };

  const handleEditFormRemoveClass = (id) => {
    setEditFormData(prev => ({
      ...prev,
      classes: prev.classes.length > 1 ? prev.classes.filter(cls => cls.id !== id) : prev.classes // Must have at least one class
    }));
  };


  const handleSaveEdit = async (characterId) => {
    if (!firestoreUser) {
      setError("User not found. Cannot save changes.");
      return;
    }

    if (!editFormData.name || !editFormData.race) {
      setError("Name and Race are required.");
      return;
    }

    const validClasses = editFormData.classes.filter(cls => cls.className && cls.classLevel >= 1);
    if (validClasses.length === 0) {
      setError("Please add at least one valid class with a level.");
      return;
    }
    if (validClasses.length !== editFormData.classes.length) {
      setError("Please ensure all added classes have a name and a level of at least 1.");
      return;
    }

    setLoading(true);
    try {      const updates = {
        name: editFormData.name,
        race: editFormData.race,
        dndBeyondLink: editFormData.dndBeyondLink || null, // Include D&D Beyond link in updates
        // Remove the temporary 'id' field from class objects before sending to service
        classes: validClasses.map(({ id, ...rest }) => rest),      };
      await updateCharacter(characterId, updates);
      
      setCharacters(prevChars =>
        prevChars.map(char =>
          char.id === characterId
            ? { ...char, ...updates, totalLevel: updates.classes.reduce((sum, cls) => sum + cls.classLevel, 0) }
            : char
        )
      );
      setEditingCharacterId(null);
      setError('');
    } catch (err) {
      console.error("Failed to save character changes:", err);
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImageFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        setNewImageFile(null);
        setImagePreview('');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size should not exceed 5MB.');
        setNewImageFile(null);
        setImagePreview('');
        return;
      }
      setNewImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError(''); // Clear general errors
    } else {
      setNewImageFile(null);
      setImagePreview('');
    }
  };

  const handleOpenImageEditor = (characterId, currentImageUrl) => {
    setEditingImageForCharacterId(characterId);
    setNewImageFile(null);
    setImagePreview(currentImageUrl || ''); // Show current image or nothing if no current image
    setError('');
  };
  const handleCancelImageEdit = () => {
    setEditingImageForCharacterId(null);
    setNewImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview('');
    setError('');
  };

  const handleImageUpload = async () => {
    if (!newImageFile || !editingImageForCharacterId) return;

    setUploadingImage(true);
    setError(''); 
    try {
      const newImageUrl = await uploadCharacterImage(editingImageForCharacterId, newImageFile);
      await updateCharacter(editingImageForCharacterId, { imageUrl: newImageUrl });

      // Update character in local state
      setCharacters(prevChars => 
        prevChars.map(char => 
          char.id === editingImageForCharacterId ? { ...char, imageUrl: newImageUrl } : char
        )
      );
      
      // Also update in App.js state if onCharacterCreated/Updated callback is available
      // For now, local update is fine, but for consistency, App.js should be the source of truth.
      // This might require lifting character update logic or a refetch in App.js.

      handleCancelImageEdit(); // Close editor and clear states
    } catch (err) {
      console.error("Error processing image upload and character update:", err);
      setError(`Failed to update character image: ${err.message}`);
    } finally {
      setUploadingImage(false);
    }
  };


  if (loading) { // Combined loading state
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)', bgcolor: '#1c1c1c' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
      </Box>
    );
  }

  // Prioritize options error if it exists
  if (optionsError) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, bgcolor: '#444444', color: '#F5F5F5', border: '1px solid #d98d2a' }}>
          <Alert severity="error" sx={{ bgcolor: '#ff6b6b', color: 'white' }}>{optionsError}</Alert>
          <Typography sx={{ mt: 2, textAlign: 'center' }}>Please try refreshing the page.</Typography>
        </Paper>
      </Container>
    );
  }

  // Character fetching error
  if (error && !characters.length && !optionsError) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, bgcolor: '#444444', color: '#F5F5F5', border: '1px solid #d98d2a' }}>
          <Alert severity="error" sx={{ bgcolor: '#ff6b6b', color: 'white' }}>{error}</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="lg" sx={{ mt: 4, mb: 4, color: '#F5F5F5', bgcolor: '#1c1c1c' }}>
      <Paper elevation={3} sx={{ p: {xs: 2, md: 3}, bgcolor: '#2a2a2a', border: '1px solid #d98d2a', fontFamily: 'Georgia, serif' }}>
        <Typography variant="h4" component="h1" sx={{ color: '#d98d2a', mb: 3, textAlign: 'center', fontFamily: 'inherit' }}>
          My Characters
        </Typography>
        {error && !optionsError && (
            <Alert severity={characters.length > 0 ? "warning" : "error"} sx={{ mb: 2, bgcolor: characters.length > 0 ? '#553C1A' : '#ff6b6b', color: '#F5F5F5' }}>{error}</Alert>
        )}
        {characters.length === 0 && !loading && !optionsError ? (
          <Typography sx={{ textAlign: 'center', fontFamily: 'inherit', color: '#F5F5F5' }}>
            You have not created any characters yet.
          </Typography>
        ) : (
          <List>
            {characters.map((char) => (
              <React.Fragment key={char.id}>
                {editingCharacterId === char.id ? (
                  <Paper elevation={4} sx={{ mb: 2, p: 2, bgcolor: '#424242', border: '1px solid #d98d2a' }}>
                    <Typography variant="h6" sx={{color: '#d98d2a', mb:1}}>Editing: {char.name}</Typography>
                    <TextField
                      label="Name"
                      name="name"
                      value={editFormData.name || ''}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      margin="normal"
                      InputLabelProps={{ style: { color: '#d98d2a' } }}
                      inputProps={{ style: { color: '#F5F5F5' } }}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d98d2a' } } }}
                    />                    <FormControl fullWidth margin="normal" sx={{ mb: 1 }}>
                      <InputLabel id={`race-edit-label-${char.id}`} sx={{ color: '#d98d2a' }}>Race</InputLabel>
                      <Select
                        labelId={`race-edit-label-${char.id}`}
                        name="race"
                        value={editFormData.race || ''}
                        label="Race"
                        onChange={handleInputChange}
                        required
                        sx={{ color: '#F5F5F5', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
                      >
                        {raceOptions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>

                    <TextField
                      label="D&D Beyond Character Link (Optional)"
                      name="dndBeyondLink"
                      value={editFormData.dndBeyondLink || ''}
                      onChange={handleInputChange}
                      fullWidth
                      type="url"
                      margin="normal"
                      placeholder="https://www.dndbeyond.com/characters/..."
                      InputLabelProps={{ style: { color: '#d98d2a' } }}
                      inputProps={{ style: { color: '#F5F5F5' } }}
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d98d2a' } } }}
                    />

                    <Typography variant="subtitle2" sx={{ color: '#d98d2a', fontFamily: 'inherit', mt: 2, mb: 1 }}>
                      Edit Classes (up to 5)
                    </Typography>
                    {editFormData.classes && editFormData.classes.map((cls, index) => (
                      <Grid container spacing={1} key={cls.id} alignItems="center" sx={{ mb: 1 }}>
                        <Grid item xs={6} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel id={`edit-class-name-label-${cls.id}`} sx={{ color: '#d98d2a' }}>Class #{index + 1}</InputLabel>
                            <Select
                              labelId={`edit-class-name-label-${cls.id}`}
                              value={cls.className}
                              label={`Class #{index + 1}`}
                              onChange={(e) => handleEditFormClassChange(cls.id, 'className', e.target.value)}
                              required
                              sx={{ color: '#F5F5F5', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
                            >
                              {classOptions.map((cOpt) => <MenuItem key={cOpt} value={cOpt}>{cOpt}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={4} sm={4}>
                          <TextField
                            label="Lvl"
                            type="number"
                            size="small"
                            value={cls.classLevel}
                            onChange={(e) => handleEditFormClassChange(cls.id, 'classLevel', e.target.value)}
                            required
                            fullWidth
                            InputLabelProps={{ style: { color: '#d98d2a' } }}
                            inputProps={{ style: { color: '#F5F5F5', min: 1 } }}
                            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d98d2a' } } }}
                          />
                        </Grid>
                        <Grid item xs={2} sm={2} sx={{ textAlign: "right" }}>
                          {editFormData.classes.length > 1 && (
                            <IconButton onClick={() => handleEditFormRemoveClass(cls.id)} sx={{ color: '#d98d2a' }} size="small">
                              <RemoveCircleOutlineIcon fontSize="small"/>
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    ))}
                    {editFormData.classes && editFormData.classes.length < 5 && (
                      <Button
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleEditFormAddClass}
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{
                          color: '#d98d2a', borderColor: '#d98d2a', fontFamily: 'inherit', mt: 1, mb:2,
                          '&:hover': { borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)' }
                        }}
                      >
                        Add Class
                      </Button>
                    )}

                    <Button onClick={() => handleSaveEdit(char.id)} variant="contained" sx={{ mr: 1, backgroundColor: '#d98d2a', color: '#1c1c1c', '&:hover': {backgroundColor: '#c87d26'} }} disabled={loading || optionsLoading || uploadingImage}>
                      {loading ? <CircularProgress size={24} color="inherit"/> : 'Save'}
                    </Button>
                    <Button onClick={handleCancelEdit} variant="outlined" sx={{color: '#F5F5F5', borderColor: '#d98d2a', '&:hover': {borderColor: '#c87d26'}}}>Cancel</Button>
                  </Paper>
                ) : (
                  <ListItem
                    component={Paper}
                    elevation={2}
                    sx={{
                      mb: 2,
                      p: {xs: 1, sm: 2},
                      bgcolor: '#3a3a3a',
                      color: '#F5F5F5',
                      border: '1px solid #555555',
                      borderRadius: '4px',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' }, // Stack on small screens
                      alignItems: 'center',
                      gap: 2,
                      '&:hover': {
                        borderColor: '#d98d2a',
                        boxShadow: '0 0 8px #d98d2a'
                      }
                    }}
                  >
                    <Avatar 
                        src={editingImageForCharacterId === char.id && imagePreview ? imagePreview : char.imageUrl || undefined} 
                        alt={char.name}
                        sx={{ 
                            width: { xs: 80, sm: 100 }, 
                            height: { xs: 80, sm: 100 }, 
                            mr: {sm: 2}, 
                            mb: {xs: 1, sm: 0},
                            border: '2px solid #d98d2a',
                            fontSize: '2.5rem'
                        }}
                        onClick={() => navigate(`/character/${char.id}`)} // Navigate on image click
                    >
                        {char.name ? char.name.charAt(0).toUpperCase() : 'C'}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, textAlign: {xs: 'center', sm: 'left'} }}>
                      <Typography 
                        variant="h6" 
                        component={RouterLink} 
                        to={`/character/${char.id}`} 
                        sx={{ color: '#d98d2a', textDecoration: 'none', '&:hover': {textDecoration: 'underline'} }}
                      >
                        {char.name}
                      </Typography>
                      <ListItemText 
                        primary={char.name}
                        secondary={formatCharacterClasses(char.classes, char.totalLevel)}
                        primaryTypographyProps={{fontFamily: 'inherit', color: '#F5F5F5', fontWeight: 'bold'}}
                        secondaryTypographyProps={{fontFamily: 'inherit', color: '#A9A9A9'}}
                      />
                    </Box>                    <Box sx={{ display: 'flex', flexDirection: {xs: 'row', sm: 'column'}, gap: 1, mt: {xs: 1, sm: 0} }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => handleEditClick(char)} 
                        sx={{color: '#d98d2a', borderColor: '#d98d2a', '&:hover': {borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)'}}}>
                          Edit Details
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => handleOpenImageEditor(char.id, char.imageUrl)} 
                        sx={{color: '#d98d2a', borderColor: '#d98d2a', '&:hover': {borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)'}}}>
                          Edit Image
                      </Button>
                      {char.dndBeyondLink && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LaunchIcon />}
                          href={char.dndBeyondLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#66bb6a',
                            borderColor: '#66bb6a',
                            fontSize: '0.7rem',
                            '&:hover': {
                              borderColor: '#4caf50',
                              backgroundColor: 'rgba(102, 187, 106, 0.1)'
                            }
                          }}
                        >
                          D&D Beyond
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                )}
                {/* Image Editing UI for this specific character */} 
                {editingImageForCharacterId === char.id && (
                  <Paper elevation={4} sx={{ mb: 2, p: 2, bgcolor: '#424242', border: '1px solid #d98d2a' }}>
                    <Typography variant="h6" sx={{color: '#d98d2a', mb:1}}>Editing Image for: {char.name}</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2, bgcolor: '#ff6b6b', color: 'white' }}>{error}</Alert>} {/* Display specific image errors here */}
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id={`character-image-upload-${char.id}`}
                      type="file"
                      onChange={handleImageFileChange}
                    />
                    <label htmlFor={`character-image-upload-${char.id}`}>
                      <Button variant="outlined" component="span" fullWidth sx={{
                        color: '#d98d2a', 
                        borderColor: '#d98d2a', 
                        fontFamily: 'inherit', 
                        mb: imagePreview ? 1 : 2,
                        '&:hover': {borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)'}
                      }}>
                        Choose New Image
                      </Button>
                    </label>
                    {imagePreview && (
                      <Box sx={{ textAlign: 'center', my: 2 }}>
                        <Avatar src={imagePreview} alt="New Preview" sx={{ width: 100, height: 100, margin: 'auto', border: '2px solid #d98d2a' }} />
                      </Box>
                    )}
                    <Button 
                      onClick={handleImageUpload} 
                      variant="contained" 
                      disabled={uploadingImage || !newImageFile}
                      sx={{ mr: 1, backgroundColor: '#d98d2a', color: '#1c1c1c', '&:hover': {backgroundColor: '#c87d26'} }}
                    >
                      {uploadingImage ? <CircularProgress size={24} color="inherit"/> : 'Upload Image'}
                    </Button>
                    <Button onClick={handleCancelImageEdit} variant="outlined" sx={{color: '#F5F5F5', borderColor: '#d98d2a', '&:hover': {borderColor: '#c87d26'}}}>Cancel</Button>
                  </Paper>
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default MyCharactersPage;
